import { NAV_CONFIG, SPECIAL_SECTIONS } from "@/constants/navigation"
import { useTheme } from "@mui/material/styles"
import useMediaQuery from "@mui/material/useMediaQuery"
import classNames from "classnames"
import PropTypes from "prop-types"
import React, { useMemo } from "react"
import { NavLink } from "react-router-dom"

export const getAuthorizedNavItems = (role, userPermissions, feature_flags) => {
  const enabled_new_dashboard = feature_flags.new_school_admin_dashboard_enabled

  const navConfig = NAV_CONFIG[role]
  if (!navConfig) return []

  const shouldUseNewDashboard = role === "school_admin" && enabled_new_dashboard
  const effectiveRole = shouldUseNewDashboard ? "school_admin_new_dashboard" : role
  return NAV_CONFIG[effectiveRole].filter(navItem => !navItem.permission || userPermissions[navItem.permission])
}

// Regular link component for when we're not in a Router context
const RegularLink = ({ to, children, className }) => {
  const currentPath = window.location.pathname

  const isActive = (() => {
    // Check if this link is part of a special section
    const specialSection = Object.values(SPECIAL_SECTIONS).find(section => {
      if (section.matcher) {
        return section.matcher(to)
      }
      return section.path === to
    })

    if (specialSection) {
      return specialSection.relatedPaths.some(path => currentPath.startsWith(path))
    }

    return currentPath.startsWith(to)
  })()

  const linkClassName = classNames(className, { active: isActive, "-active": isActive })

  return (
    <a href={to} className={linkClassName} data-turbolinks="false" aria-current={isActive ? "page" : undefined}>
      {children}
    </a>
  )
}

const NavbarLinks = ({ currentUser, hasNewMessage, hasSessionRequests, hasInterviewRequests, useRegularLinks }) => {
  const userRole = currentUser?.role
  const { permissions: userPermissions, feature_flags } = currentUser

  const newEvents = { hasNewMessage, hasSessionRequests, hasInterviewRequests }
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  const navItems = useMemo(
    () => getAuthorizedNavItems(userRole, userPermissions, feature_flags),
    [userRole, userPermissions, feature_flags]
  )

  const LinkComponent = useRegularLinks ? RegularLink : NavLink

  return (
    <nav
      className={classNames({
        "mobile-nav": isMobile,
        "main-nav": !isMobile,
      })}
      aria-label="Main navigation"
    >
      {navItems.map(({ path, label, hasNew }) => (
        <React.Fragment key={path}>
          <LinkComponent
            to={path}
            className={
              useRegularLinks ? undefined : ({ isActive }) => classNames({ active: isActive, "-active": isActive })
            }
          >
            {label}
          </LinkComponent>
          {hasNew && newEvents[hasNew] && (
            <span className="red-dot" aria-label="New notification">
              New!
            </span>
          )}
        </React.Fragment>
      ))}
    </nav>
  )
}

NavbarLinks.propTypes = {
  currentUser: PropTypes.shape({
    role: PropTypes.string,
    permissions: PropTypes.object,
    feature_flags: PropTypes.object,
  }).isRequired,
  hasNewMessage: PropTypes.bool,
  hasSessionRequests: PropTypes.bool,
  hasInterviewRequests: PropTypes.bool,
  useRegularLinks: PropTypes.bool,
}

NavbarLinks.defaultProps = {
  hasNewMessage: false,
  hasSessionRequests: false,
  hasInterviewRequests: false,
  useRegularLinks: false,
}

export default NavbarLinks
